import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
import { RequestStatus, useUCSuggestions } from '@atlassian/navigation-bff-data-provider';

import { useMenuStateContext } from '../../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
} from '../../../../hooks';

import { useFetchUser } from './useFetchUser';
import { usePrefetchLoomPlaceholder as usePrefetchLoom } from './usePrefetchLoom';

interface usePrefetchV1Return {
	triggerPrefetch: () => Promise<void>;
}

/**
 * V1 of prefetch that will try to call the backend endpoint to get list of menu items
 * Loom is NOT included. TODO: add loom preload separately https://hello.jira.atlassian.cloud/browse/NAVX-2470
 * User is conditionally fetched based on return items from the backend
 *
 * @returns Object containing triggerPrefetch function
 */
export function usePrefetchV1(): usePrefetchV1Return {
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { product, suggestionConfig, subProduct } = useMenuStateContext();

	const isPrefetchEnabled = suggestionConfig.enabled && suggestionConfig.prefetch === true;
	const { locale } = useIntl();
	const onResultChange = suggestionConfig.enabled ? suggestionConfig?.onResultChange : undefined;

	const { triggerPrefetch: triggerPrefetchLoom } = usePrefetchLoom();

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	/**
	 * Fetching V1 version of menu items
	 */
	const { status, data, isLoading, hasError, fetchUCSuggestions } = useUCSuggestions({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		orgId: suggestionConfig.enabled ? suggestionConfig.orgId : '',
		userId: suggestionConfig.enabled ? suggestionConfig.accountId : '',
		product,
		locale,
		subProduct: suggestionConfig.enabled ? subProduct : undefined,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const prefetchTriggered = useRef(false);

	const shouldFetchSuggestions =
		crossAppCreationEnabled && isPrefetchEnabled && suggestionConfig.enabled;

	const { fetchUserIfNeeded } = useFetchUser();

	const triggerPrefetch = useCallback(async () => {
		if (prefetchTriggered.current) {
			return;
		}

		prefetchTriggered.current = true;

		if (shouldFetchSuggestions) {
			fetchUCSuggestions();
		}

		fetchUserIfNeeded({ items: data?.items });
		triggerPrefetchLoom();
	}, [
		triggerPrefetchLoom,
		fetchUserIfNeeded,
		fetchUCSuggestions,
		data?.items,
		shouldFetchSuggestions,
	]);

	const bffHasInitialized = useMemo(() => {
		return status !== RequestStatus.INIT;
	}, [status]);

	useEffect(
		function notifySuggestionResultChange() {
			if (!shouldFetchSuggestions) {
				return;
			}

			if (onResultChange && bffHasInitialized) {
				onResultChange?.({ data, isLoading, hasError });
			}
		},
		[onResultChange, data, isLoading, hasError, bffHasInitialized, shouldFetchSuggestions],
	);

	return {
		triggerPrefetch,
	};
}
